import React from "react";
import { Pencil, ChevronDown } from "lucide-react";
import { useGetApplicationQuery } from "@/store/service";
import { useParams } from "react-router-dom";
import { Controller, UseFormReturn } from "react-hook-form";
import ApiApplicationApplicant from "@/model/interface/api/ApiApplicationApplicant";
import DatePicker from "react-datepicker";
import CustomSelect from "@/components/shared/CustomSelect";
import { MaritalStatus } from "@/model/interface/form/MaritalStatus";
import { checkEmail } from "@/util/validation";
import Applicants from "@/model/interface/form/Applicants";

const ApplicationApplicant = ({
  form,
  type,
}: {
  form: UseFormReturn<Applicants>;
  type: "main-applicant" | "co-applicant";
}) => {
  const params = useParams();
  const { data: application } = useGetApplicationQuery(params.id!);
  const mainApplicant =
    application?.applicants?.find((applicant) => applicant.type === "MainApplicant") ?? ({} as ApiApplicationApplicant);
  const coApplicant =
    application?.applicants?.find((applicant) => applicant.type === "CoApplicant") ?? ({} as ApiApplicationApplicant);
  const applicant = type === "co-applicant" ? coApplicant : mainApplicant;
  const applicantType = type === "co-applicant" ? "coApplicant" : "mainApplicant";
  const { control, watch, register, formState, handleSubmit } = form;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">First name</h6>
          <input
            type="text"
            {...register(`${applicantType}.firstName`, { value: applicant?.firstName })}
            className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors[applicantType]?.firstName ? "border-red-400" : "border-gray-300"}`}
            placeholder="First name"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantType]?.firstName && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.firstName?.message}</p>
          )}
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Last name</h6>
          <input
            type="text"
            {...register(`${applicantType}.lastName`, { value: applicant?.lastName })}
            className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors[applicantType]?.lastName ? "border-red-400" : "border-gray-300"}`}
            placeholder="Last name"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantType]?.lastName && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.lastName?.message}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Date of birth</h6>
          <Controller
            control={control}
            name={`${applicantType}.dateOfBirth`}
            render={({ field }) => (
              <DatePicker
                wrapperClassName="block w-full"
                className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors[applicantType]?.dateOfBirth ? "border-red-400" : "border-gray-300"}`}
                placeholderText="Select date of birth"
                onChange={(date) => field.onChange(date)}
                selected={field.value}
              />
            )}
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantType]?.dateOfBirth && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.dateOfBirth?.message}</p>
          )}
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Marital status</h6>
          <Controller
            control={control}
            name={`${applicantType}.maritalStatus`}
            render={({ field }) => (
              <CustomSelect
                value={MaritalStatus.find((ms) => ms.value === field.value)!}
                onChange={(o: MaritalStatus) => field.onChange(o.value)}
                options={MaritalStatus}
                mapOptionToLabel={(o: MaritalStatus) => o.label}
                mapOptionToValue={(o: MaritalStatus) => o.value}
              />
            )}
          />
          <ChevronDown className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantType]?.maritalStatus && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.maritalStatus?.message}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Email address</h6>
          <input
            type="text"
            {...register(`${applicantType}.emailAddress`, {
              onBlur: (event) => checkEmail(event),
            })}
            className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors[applicantType]?.emailAddress ? "border-red-400" : "border-gray-300"}`}
            placeholder="Email"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantType]?.emailAddress && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.emailAddress?.message}</p>
          )}
        </div>
        <div className="relative">
          <h6 className="text-sm text-gray-500 mb-1">Phone</h6>
          <input
            type="text"
            {...register(`${applicantType}.phone`, { value: applicant?.phone })}
            className={`w-full h-11 rounded-lg border bg-white px-4 pr-10 text-sm outline-none transition-colors focus:border-[#0FD131] focus:ring-2 focus:ring-[#9BFBE0] ${formState.errors[applicantType]?.phone ? "border-red-400" : "border-gray-300"}`}
            placeholder="Phone"
          />
          <Pencil className="absolute right-3 top-9 size-3.5 text-gray-400 pointer-events-none" />
          {formState.errors[applicantType]?.phone && (
            <p className="mt-1 text-sm text-red-500">{formState.errors[applicantType]?.phone?.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationApplicant;
